import { customerBaseApi } from "@/react/services/api";
import { handleMutationResponse } from "@/react/services/api/handleMutationResponse";
import { KEY } from "@/react/components/table/useTableMutations";
import {
  DESTINATION_TYPES,
  EXTENSION_TYPES,
  EXTENSIONS,
} from "@/react/features/support-tools/extensions/constants";
import { DURATION, notify, TYPE } from "@8x8/oxygen-toaster";
import lodash from "lodash";
import { createNotifier } from "@/react/notifications";

const NUMBER = "NUMBER";

const extensionsUrl = (pbxId, category = null) =>
  `pbxes/${pbxId}/extensions${category ? `/${category}` : ""}`;

const isForwardingRuleSupported = (forwardingRule) => {
  return !forwardingRule || forwardingRule?.hasExactlyOneDestination;
};

export const extensionsApi = customerBaseApi.injectEndpoints({
  endpoints: (build) => ({
    getMultiCallSupportExtensions: build.query({
      query: (pbxId) => ({
        url: extensionsUrl(pbxId, EXTENSION_TYPES.ALLOWED),
        method: "GET",
      }),
      providesTags: [EXTENSION_TYPES.ALLOWED],
    }),
    getCallWaitingExtensions: build.query({
      query: (pbxId) => ({
        url: extensionsUrl(pbxId, EXTENSION_TYPES.CALL_WAITING),
        method: "GET",
      }),
      providesTags: [EXTENSION_TYPES.CALL_WAITING],
    }),
    getForwardingRulesExtensions: build.query({
      query: (pbxId) => ({
        url: extensionsUrl(pbxId, EXTENSION_TYPES.FORWARDING_RULES),
        method: "GET",
      }),
      transformResponse: (response) => {
        const supportedExtensions = [];
        const unsupportedExtensions = [];
        // Backend DTO and underlying services use "NUMBER" as destination type
        // for those who are extension numbers. Change "NUMBER" to "EXTENSION"
        // [TAP-2671] for eliminating confusion in UI (since there is already
        // one called EXTERNAL_NUMBER)
        const updateDestinationType = (rule) => {
          if (rule?.destination?.destinationType === NUMBER) {
            rule.destination.destinationType = DESTINATION_TYPES.EXTENSION;
          }
        };

        response.forEach((extension) => {
          const { BUSY, OUTAGE, NO_ANSWER } = extension.forwardingRules;
          [BUSY, OUTAGE, NO_ANSWER].forEach(updateDestinationType);
          const allRulesSupported = [BUSY, OUTAGE, NO_ANSWER].every(
            isForwardingRuleSupported
          );
          if (allRulesSupported) {
            supportedExtensions.push(extension);
          } else {
            unsupportedExtensions.push(extension);
          }
        });

        if (unsupportedExtensions.length > 0) {
          const unsupportedList = unsupportedExtensions
            .map((extension) => extension.extension.subscriptionId)
            .join(", ");
          notify({
            type: TYPE.warning,
            content: `The following extensions have at least a forwarding rule with more than one destination, and are not displayed in the table: ${unsupportedList}`,
            duration: DURATION.INFINITE,
          });
        }

        return supportedExtensions;
      },

      providesTags: [EXTENSION_TYPES.FORWARDING_RULES],
    }),
    updateForwardingRulesExtensions: build.mutation({
      query: ({ pbxId, extensions }) => ({
        url: extensionsUrl(pbxId, EXTENSION_TYPES.FORWARDING_RULES),
        method: "PUT",
        // Replace destinationType 'EXTENSION' with 'NUMBER' (DTO)
        body: lodash.cloneDeepWith(extensions, (value) => {
          if (
            lodash.isObject(value) &&
            value.destinationType === DESTINATION_TYPES.EXTENSION
          ) {
            return { ...value, destinationType: NUMBER };
          }
        }),
      }),
      invalidatesTags: [EXTENSION_TYPES.FORWARDING_RULES],
    }),
    getRecordingSettingsExtensions: build.query({
      query: (pbxId) => ({
        url: extensionsUrl(pbxId, EXTENSION_TYPES.RECORDING_SETTINGS),
        method: "GET",
      }),
      providesTags: [EXTENSION_TYPES.RECORDING_SETTINGS],
    }),
    updateRecordingSettingsExtensions: build.mutation({
      query: ({ pbxId, extensions }) => ({
        url: extensionsUrl(pbxId, EXTENSION_TYPES.RECORDING_SETTINGS),
        method: "PUT",
        body: extensions,
      }),
      invalidatesTags: [EXTENSION_TYPES.RECORDING_SETTINGS],
    }),
    patchExtensions: build.mutation({
      query: ({ pbxId, patchOptions }) => ({
        url: extensionsUrl(pbxId),
        method: "PATCH",
        body: patchOptions,
      }),
      invalidatesTags: [EXTENSION_TYPES.CALL_WAITING, EXTENSION_TYPES.ALLOWED],
    }),
    getAvailableExtensions: build.query({
      query: (pbxId) => ({
        url: extensionsUrl(pbxId, "service-type"),
        method: "GET",
      }),
      providesTags: [EXTENSIONS],
    }),
  }),
});

export const {
  useGetMultiCallSupportExtensionsQuery,
  useGetCallWaitingExtensionsQuery,
  useGetForwardingRulesExtensionsQuery,
  useGetRecordingSettingsExtensionsQuery,
  useGetAvailableExtensionsQuery,
  useUpdateForwardingRulesExtensionsMutation,
  useUpdateRecordingSettingsExtensionsMutation,
  usePatchExtensionsMutation,
} = extensionsApi;

export const handleUpdateResponse = ({
  rowsToUpdate,
  setRowsToUpdate,
  setData,
  idKey,
}) =>
  handleMutationResponse({
    rows: rowsToUpdate,
    setRows: setRowsToUpdate,
    setData,
    responseMappingKey: idKey,
    idKey: KEY,
    notifyContent: createNotifier("update", "extension"),
  });
